import { useContext, useMemo, type ReactNode } from 'react'

import MaldivesCharterCarousel from 'home/components/OfferCarousels/MaldivesCharterCarousel'
import { FlightsClassTypes } from 'constants/flight'
import useMaldivesCharterRecommendedOfferList from 'hooks/MaldivesBundle/useMaldivesCharterRecommendedOfferList'
import useOffers from 'hooks/Offers/useOffers'
import { sortBy } from 'lib/array/arrayUtils'
import { REWARD_PREVIEW_RATE_BY_CABIN } from 'lib/maldivesCharter/classicRewardPricing'
import LoyaltySpendPointsPricingContext from 'loyalty/contexts/LoyaltySpendPointsPricingContext'
import type { SpendPointsSortOption } from './loyaltySpendPointsTypes'
import { MINIMUM_OFFERS_TO_SHOW_CAROUSEL } from './loyaltySpendPointsConstants'

interface Props {
  title: ReactNode
  sortOption: SpendPointsSortOption
}

const SORT_DIRECTION_BY_OPTION: Record<SpendPointsSortOption, 'asc' | 'desc'> = {
  recommended: 'asc',
  'lowest-points': 'asc',
  'highest-points': 'desc',
}

function LoyaltySpendPointsMaldivesCarousel({ title, sortOption }: Props) {
  const { burnPointsLimit, showOffersWithinBalance } = useContext(LoyaltySpendPointsPricingContext)
  const maldivesOfferList = useMaldivesCharterRecommendedOfferList({ includeBundleStream: false })

  const filteredOfferList = useMemo<App.OfferList>(() => {
    if (!showOffersWithinBalance || burnPointsLimit === undefined) return maldivesOfferList

    const pointsPerPerson = REWARD_PREVIEW_RATE_BY_CABIN[FlightsClassTypes.ECONOMY]?.pointsPerPerson ?? 0

    return {
      ...maldivesOfferList,
      offerIds: pointsPerPerson <= burnPointsLimit ? maldivesOfferList.offerIds : [],
    }
  }, [burnPointsLimit, maldivesOfferList, showOffersWithinBalance])

  const [offers, offersFetching] = useOffers<App.HotelOffer>(filteredOfferList.offerIds)

  const allOffersLoaded = !filteredOfferList.fetching
    && !offersFetching
    && offers.length > 0
    && offers.length === filteredOfferList.offerIds.length

  // Hold back with empty IDs until all offer details are loaded so tiles render
  // in sorted order from the start rather than jumping when the sort runs.
  // key={sortOption} on MaldivesCharterCarousel resets scroll on sort change.
  const overrideOfferList = useMemo<App.OfferList>(() => {
    if (!allOffersLoaded) {
      return { ...filteredOfferList, fetching: true, offerIds: [] }
    }

    const priceById = new Map(
      offers.map(offer => [offer.id, offer.lowestPricePackage?.price ?? Number.MAX_VALUE]),
    )

    return {
      ...filteredOfferList,
      offerIds: sortBy(
        filteredOfferList.offerIds,
        id => priceById.get(id) ?? Number.MAX_VALUE,
        SORT_DIRECTION_BY_OPTION[sortOption],
      ),
    }
  }, [allOffersLoaded, filteredOfferList, offers, sortOption])

  if (showOffersWithinBalance) {
    if (overrideOfferList.fetching) return null
    if (overrideOfferList.offerIds.length < MINIMUM_OFFERS_TO_SHOW_CAROUSEL) return null
  }

  return <MaldivesCharterCarousel
    key={sortOption}
    overrideTitle={title}
    overrideOfferList={overrideOfferList}
    useRewardPreviewPricing
    hideWhenEmpty={false}
    includeBundleStream={false}
  />
}

export default LoyaltySpendPointsMaldivesCarousel
